/**
 * If a screen throws while drawing, React would leave a blank page: show a way back instead, and
 * send the error to Sentry (docs/architecture.md §14).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { reportError } from '../../platform/analytics';
import { diag } from '../../platform/diag';
import ui from './ui.module.css';
import s from './CrashScreen.module.css';

function Crashed() {
  const { t } = useTranslation();
  return (
    <div className={s.crash} role="alert">
      <div className={s.box}>
        <h1 className={s.title}>{t('common.crash.title')}</h1>
        <p className={s.body}>{t('common.crash.body')}</p>
        <button type="button" className={[ui.primary, s.reload].join(' ')} onClick={() => location.reload()}>
          {t('common.crash.reload')}
        </button>
      </div>
    </div>
  );
}

export class CrashBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    diag.error(`crash: ${String(error)}`);
    reportError(error, { componentStack: info.componentStack });
  }

  override render(): ReactNode {
    return this.state.failed ? <Crashed /> : this.props.children;
  }
}
